import { FormGroup, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { computeSelectProps } from '@frontend/components/fields/SelectField';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';
import type { IReduxState } from '../../../../configureStore';
import type { IOption } from '../../../interfaces/option';
import type { ISpecialism } from '../../../interfaces/specialism';
import type { GeneralSettingsProps } from './generalSettingsProps';
import { LanguageOptions } from '../../../../../common/enums/languageOptions';
import { Profession } from '../../../../../common/enums/profession';
import { getSpecialismSelectOptions } from '../../../../utils/getSpecialismSelectOptions';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { GeneralSettingsReadOnlyFormGroup } from './GeneralSettingsReadOnlyFormGroup';

type SpecialismOption = IOption<string>;

export function GeneralSettingsSpecialism(props: Readonly<GeneralSettingsProps>) {
    const abortableFetch = useAbortableFetch();
    const [selectedSpecialism, setSelectedSpecialism] = useState<string | null>(null);
    const [specialismOptions, setSpecialismOptions] = useState<SpecialismOption[]>([]);
    const language = useSelector((state: IReduxState) => state.language.code);

    const translate = getSelectedLanguage();

    useEffect(() => {
        if (props.employment.profession) {
            initializeEmploymentSpecialismOption(props.employment);
        }

        loadSpecialisms();
    }, [props.employment.profession, props.isEditing]);

    if (props.employment?.profession !== Profession.medicalSpecialist) {
        return null;
    }

    if (!props.isEditing) {
        return (
            <GeneralSettingsReadOnlyFormGroup
                label={translate.common.specialism}
                value={specialismOptions.find((option) => option.value === selectedSpecialism)?.label ?? null}
            />
        );
    }

    return (
        <FormGroup
            hasErrors={props.errors.has('specialismCode')}
            isRequired={true}
            label={translate.common.specialism}
        >
            <Select
                {...computeSelectProps({
                    error: getValidationErrorTranslation(props.errors.get('specialismCode')),
                    onChange: handleChange,
                    options: specialismOptions,
                    placeholder: translate.common.clickAndChooseFromList,
                    value: selectedSpecialism,
                })}
            />
        </FormGroup>
    );

    function handleChange(value: string | null) {
        setSelectedSpecialism(value);
        props.onChange('specialismCode', value);
        props.onValidateOne('specialismCode', value);
    }

    function getSpecialismOption(specialism: ISpecialism) {
        return { label: getSpecialismText(specialism), value: specialism.code };
    }

    function getSpecialismText(specialism: ISpecialism) {
        return language === LanguageOptions.nl ? specialism.descriptionNL : specialism.descriptionEN;
    }

    async function initializeEmploymentSpecialismOption(employment: GeneralSettingsProps['employment']) {
        let specialismOption: SpecialismOption | null = null;
        if (employment.profession === Profession.medicalSpecialist && employment.specialismCode) {
            const specialismUrl = `/api/dhdThesaurus/specialisms/${employment.specialismCode}`;
            const specialism = await abortableFetch.getJson(specialismUrl);

            specialismOption = getSpecialismOption(specialism.json);
        }

        setSelectedSpecialism(specialismOption?.value ?? null);
    }

    async function loadSpecialisms() {
        const result = await abortableFetch.fetchJson(`/api/dhdThesaurus/specialisms?language=${language}`, 'GET');
        const specialisms = result.json.items.filter((item: ISpecialism) => item.isActive) as ISpecialism[];
        const specialismSelectOptions = getSpecialismSelectOptions(specialisms, false);

        setSpecialismOptions(
            specialismSelectOptions.map((specialism) => ({ label: specialism.text, value: specialism.value }))
        );
    }
}
